/**
 * Smart \begin ↔ \end matching for Monaco Editor.
 *
 * When the cursor sits on a \begin{env} or \end{env}, the matching
 * counterpart is highlighted with a colored decoration. This is NOT
 * the built-in "same word highlight" — it uses the AST/regex-based
 * environment tree from latex-parser to find the actual matching pair.
 *
 * Usage:
 *   Call `attachBeginEndMatching(editor, monaco)` after the editor mounts.
 *   It returns a disposable to tear down listeners.
 */

import type { editor, IDisposable } from "monaco-editor"
import { flattenEnvironments, parseLatex } from "./latex-parser"
import { LATEX_LANGUAGE_ID } from "./monaco-latex"

/* ── Decoration styling ── */

const MATCH_DECORATION_CLASS = "latex-begin-end-match"
const MATCH_DECORATION_CSS = `
.${MATCH_DECORATION_CLASS} {
  background-color: rgba(91, 140, 255, 0.18);
  border-bottom: 2px solid #5B8CFF;
  border-radius: 2px;
}
.${MATCH_DECORATION_CLASS}-dark {
  background-color: rgba(91, 140, 255, 0.15);
  border-bottom: 2px solid #5B8CFF;
  border-radius: 2px;
}
`

let styleInjected = false
function ensureStyles() {
  if (styleInjected) return
  if (typeof document === "undefined") return
  const style = document.createElement("style")
  style.textContent = MATCH_DECORATION_CSS
  document.head.appendChild(style)
  styleInjected = true
}

/* ── Core matching logic ── */

interface MatchRange {
  startLine: number
  startCol: number
  endLine: number
  endCol: number
}

/**
 * Find the matching \begin/\end span for a cursor position.
 */
function findMatch(
  source: string,
  cursorLine: number,
  cursorCol: number,
): { begin: MatchRange; end: MatchRange } | null {
  const result = parseLatex(source)
  const flat = flattenEnvironments(result.environments)

  for (const env of flat) {
    // Compute the text ranges for \begin{name} and \end{name}
    const beginText = `\\begin{${env.name}}`
    const endText = `\\end{${env.name}}`

    const beginRange: MatchRange = {
      startLine: env.startLine,
      startCol: env.startCol,
      endLine: env.startLine,
      endCol: env.startCol + beginText.length,
    }

    const endRange: MatchRange = {
      startLine: env.endLine,
      startCol: env.endCol,
      endLine: env.endLine,
      endCol: env.endCol + endText.length,
    }

    // Check if cursor is on the \begin line within the command text
    if (
      cursorLine === beginRange.startLine &&
      cursorCol >= beginRange.startCol &&
      cursorCol <= beginRange.endCol
    ) {
      return { begin: beginRange, end: endRange }
    }

    // Check if cursor is on the \end line within the command text
    if (
      cursorLine === endRange.startLine &&
      cursorCol >= endRange.startCol &&
      cursorCol <= endRange.endCol
    ) {
      return { begin: beginRange, end: endRange }
    }
  }

  return null
}

/* ── Public API ── */

/**
 * Attach \begin ↔ \end matching decorations to a Monaco editor instance.
 * Returns a disposable to clean up event listeners.
 */
export function attachBeginEndMatching(
  editorInstance: editor.IStandaloneCodeEditor,
  monaco: typeof import("monaco-editor"),
): IDisposable {
  ensureStyles()

  let decorationIds: string[] = []

  const updateDecorations = () => {
    const model = editorInstance.getModel()
    if (!model) return
    if (model.getLanguageId() !== LATEX_LANGUAGE_ID) {
      decorationIds = editorInstance.deltaDecorations(decorationIds, [])
      return
    }

    const position = editorInstance.getPosition()
    if (!position) {
      decorationIds = editorInstance.deltaDecorations(decorationIds, [])
      return
    }

    const source = model.getValue()
    const match = findMatch(source, position.lineNumber, position.column)

    if (!match) {
      decorationIds = editorInstance.deltaDecorations(decorationIds, [])
      return
    }

    const newDecorations: editor.IModelDeltaDecoration[] = [
      {
        range: new monaco.Range(
          match.begin.startLine,
          match.begin.startCol,
          match.begin.endLine,
          match.begin.endCol,
        ),
        options: {
          className: MATCH_DECORATION_CLASS,
          isWholeLine: false,
          stickiness:
            monaco.editor.TrackedRangeStickiness.NeverGrowsWhenTypingAtEdges,
        },
      },
      {
        range: new monaco.Range(
          match.end.startLine,
          match.end.startCol,
          match.end.endLine,
          match.end.endCol,
        ),
        options: {
          className: MATCH_DECORATION_CLASS,
          isWholeLine: false,
          stickiness:
            monaco.editor.TrackedRangeStickiness.NeverGrowsWhenTypingAtEdges,
        },
      },
    ]

    decorationIds = editorInstance.deltaDecorations(decorationIds, newDecorations)
  }

  // Debounce to avoid excessive re-parses
  let timer: ReturnType<typeof setTimeout> | null = null
  const debouncedUpdate = () => {
    if (timer) clearTimeout(timer)
    timer = setTimeout(updateDecorations, 80)
  }

  const disposables: IDisposable[] = []

  disposables.push(editorInstance.onDidChangeCursorPosition(debouncedUpdate))
  disposables.push(
    editorInstance.onDidChangeModelContent(() => {
      // Re-parse after content changes (slightly longer debounce)
      if (timer) clearTimeout(timer)
      timer = setTimeout(updateDecorations, 250)
    }),
  )

  // Initial decoration
  updateDecorations()

  return {
    dispose() {
      for (const d of disposables) d.dispose()
      if (timer) clearTimeout(timer)
      decorationIds = editorInstance.deltaDecorations(decorationIds, [])
    },
  }
}
